import React from 'react';
import { useSidebar } from '../../contexts/SidebarContext';

/**
 * PageHeader — Cabeçalho padrão premium (réplica EXATA do topo da página de Áudio).
 * Ver DESIGN_SYSTEM.md §3: badge 38px preenchido com o degradê da marca
 * (#0DAEFF → #3B82F6) + ícone BRANCO 19px, título 19px/600, subtítulo 12.5px.
 *
 * Props:
 *   icon: React node (lucide) — cor/tamanho são NORMALIZADOS aqui (branco, 19px),
 *         então não importa o que a página passar.
 *   title: string
 *   description?: string
 *   badge?: string | React node (pílula azul à direita)
 *   titleBadge?: string (pílula ao lado do título)
 *   children?: React node (ações extras à direita)
 */
const PageHeader = ({
  icon,
  title,
  description,
  badge,
  titleBadge,
  children,
}) => {
  const { isMobile } = useSidebar();

  // Normaliza o ícone para o padrão: branco, 19px, stroke 2.2 — independente
  // do que a página passar (muitas passam azul; sobre o degradê tem que ser branco).
  const normalizedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon, {
        size: isMobile ? 17 : 19,
        color: '#fff',
        strokeWidth: 2.2,
        style: { ...(icon.props.style || {}), color: '#fff' },
      })
    : icon;

  const pillStyle = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--dp-blue-500, #0DAEFF)',
    background: 'rgba(13, 174, 255, 0.08)',
    border: '1px solid rgba(13, 174, 255, 0.22)',
    padding: '5px 13px',
    borderRadius: '9999px',
    fontFamily: 'var(--dp-font)',
    whiteSpace: 'nowrap',
    letterSpacing: '0.3px',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      marginBottom: isMobile ? '16px' : '18px',
      gap: isMobile ? '12px' : '16px',
    }}>
      {/* Left side: icon + text */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: 0,
        flex: 1,
      }}>
        {/* Badge do ícone — degradê da marca, ícone branco (padrão Áudio) */}
        {icon && (
          <div style={{
            width: isMobile ? '34px' : '38px',
            height: isMobile ? '34px' : '38px',
            borderRadius: '10px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
            boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
            fontSize: isMobile ? '15px' : '17px',
          }}>
            {normalizedIcon}
          </div>
        )}

        {/* Title + description */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
            <h1 style={{
              fontSize: isMobile ? '17px' : '19px',
              fontWeight: 600,
              letterSpacing: '-0.3px',
              color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)',
              margin: 0,
              lineHeight: 1.2,
            }}>
              {title}
            </h1>
            {/* titleBadge: pílula ao lado do título (ex: contagem do Niche Finder). */}
            {titleBadge && (
              <span style={{ ...pillStyle, fontSize: '11px', padding: '3px 10px' }}>
                {titleBadge}
              </span>
            )}
          </div>
          {description && (
            <p style={{
              fontSize: isMobile ? '12px' : '12.5px',
              color: 'var(--dp-text-tertiary)',
              fontFamily: 'var(--dp-font)',
              margin: '1px 0 0 0',
              lineHeight: 1.4,
            }}>
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Right side: badge + extra children */}
      {(badge || children) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}>
          {badge && (
            <span style={pillStyle}>
              {badge}
            </span>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
